"use client";

import React, { useState, useEffect } from 'react';
import { Flame, Menu, Moon, Sun, X } from "lucide-react";
import { Link } from "@/components/common/Link";
import { WvcLogo } from '@/integrations/wordpress/WvcLogo';
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Toggle } from "@/components/ui/toggle";
import { CartIcon } from "@/components/shop/CartIcon";
function DesktopMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="hidden md:flex gap-4">
        {[1, 2, 3, 4].map((_, i) => <div key={i} data-index={i} className="h-6 w-20 bg-muted animate-pulse rounded-md" />)}
      </div>;
  }
  return <NavigationMenu viewport={false} className="hidden md:flex">
      <NavigationMenuList className="gap-6">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="text-base font-semibold font-default text-foreground transition-colors hover:text-primary relative py-2 after:absolute after:bottom-0 after:left-0 after:h-[2px] after:w-0 hover:after:w-full after:bg-primary after:transition-all after:duration-500">
                {item.label}
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer({
  isOpen,
  setIsOpen
}: {
  isOpen: boolean;
  setIsOpen: (v: boolean) => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (!isOpen) return null;
  return <div className="absolute top-full left-0 w-full bg-background/95 backdrop-blur-xl border-b border-border shadow-lg md:hidden flex flex-col py-4 px-4 gap-4 z-50">
      {loading ? <div className="flex flex-col gap-4">
          {[1, 2, 3, 4].map((_, i) => <div key={i} data-index={i} className="h-10 w-full bg-muted animate-pulse rounded-md" />)}
        </div> : <nav className="flex flex-col gap-2">
          {menuItems.map((item, i) => <Link key={item.id} data-index={i} to={item.href || "#"} onClick={() => setIsOpen(false)} className="text-lg font-bold font-default text-foreground py-3 border-b border-border/50 hover:text-primary transition-colors">
              {item.label}
            </Link>)}
        </nav>}
      <div className="flex items-center justify-between pt-4">
        <span className="text-sm font-medium text-muted-foreground">Display Mode</span>
        <Toggle variant="outline" size="sm" className="rounded-lg">
          <Sun className="h-4 w-4 dark:hidden" />
          <Moon className="h-4 w-4 hidden dark:block" />
        </Toggle>
      </div>
    </div>;
}
export default function MainNavigation() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest('a[href]') as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute('href');
        if (!href) return;
        if (href === '/#') {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: 'smooth'
          });
          return;
        }
        let hash = '';
        if (href.startsWith('#')) {
          hash = href;
        } else if (href.startsWith('/') && href.includes('#')) {
          const [path, hashPart] = href.split('#');
          if (path === '/' || path === window.location.pathname) {
            hash = '#' + hashPart;
          }
        }
        if (hash && hash !== '#') {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: 'smooth',
              block: 'start'
            });
            setIsMobileMenuOpen(false);
          }
        }
      }
    };
    document.addEventListener('click', handleAnchorClick);
    return () => document.removeEventListener('click', handleAnchorClick);
  }, []);
  return <section data-section-id="265150" id="main-navigation" className={`dark sticky top-0 z-50 w-full transition-all duration-500 bg-background text-foreground ${isScrolled ? 'bg-background/80 backdrop-blur-md border-b border-border shadow-md' : 'border-b border-transparent'}`}>
      <header className="container mx-auto px-4 h-20 flex items-center justify-between relative">
        
        <div className="flex items-center gap-4 z-10">
          <Link to="/" className="flex items-center group">
            <div className="relative">
              <div className="absolute -inset-2 bg-primary/10 rounded-lg blur-md opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              <WvcLogo className="h-10 w-auto relative z-10 text-foreground" />
            </div>
          </Link>
        </div>

        <div className="hidden md:flex items-center justify-center flex-1 px-8">
          <MenuProvider menu_id="77">
            <DesktopMenuConsumer />
          </MenuProvider>
        </div>

        <div className="flex items-center gap-3 z-10">
          <Badge variant="default" className="hidden sm:flex bg-secondary text-secondary-foreground border-none px-3 py-1 rounded-md shadow-sm">
            <Flame className="h-3.5 w-3.5 mr-1.5" />
            <span className="font-semibold tracking-wide text-xs">NEW DROPS</span>
          </Badge>
          
          <Toggle variant="outline" size="sm" className="hidden md:flex rounded-lg border-border/50 hover:bg-accent">
            <Sun className="h-4 w-4 dark:hidden text-foreground" />
            <Moon className="h-4 w-4 hidden dark:block text-foreground" />
          </Toggle>

          <CartIcon variant="ghost" size="icon" className="text-foreground" />

          <Button variant="ghost" size="icon" className="md:hidden text-foreground hover:bg-accent hover:text-primary transition-colors" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} aria-label="Toggle menu">
            {isMobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </Button>
        </div>

        <MenuProvider menu_id="77">
          <MobileMenuConsumer isOpen={isMobileMenuOpen} setIsOpen={setIsMobileMenuOpen} />
        </MenuProvider>

      </header>
    </section>;
}